import { Button } from "@/components/ui/button.tsx";
import { Checkbox, CheckboxField } from "@/components/ui/checkbox.tsx";
import { Modal, ModalBody, ModalContent, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/components/ui/modal.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import type { ButtonProps } from "@/components/ui/button.tsx";
import { useCallback, useState } from "react";

export interface ConfirmDialogProps {
    isOpen: boolean;
    title: string;
    description?: string;
    confirmLabel: string;
    cancelLabel: string;
    confirmIntent?: ButtonProps["intent"];
    confirmationLabel?: string;
    isConfirming?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    confirmName?: string;
    cancelName?: string;
}

export function ConfirmDialog({ isOpen, title, description, confirmLabel, cancelLabel, confirmIntent = "primary", confirmationLabel, isConfirming = false, onConfirm, onCancel, confirmName, cancelName }: ConfirmDialogProps) {
    const [isAffirmed, setIsAffirmed] = useState(false);
    const [wasConfirming, setWasConfirming] = useState(false);
    const [wasOpen, setWasOpen] = useState(isOpen);

    if (isConfirming !== wasConfirming) {
        setWasConfirming(isConfirming);

        if (!isConfirming) {
            setIsAffirmed(false);
        }
    }

    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);

        if (!isOpen) {
            setIsAffirmed(false);
        }
    }

    const handleCancel = useCallback(() => {
        setIsAffirmed(false);
        onCancel();
    }, [onCancel]);

    const handleOpenChange = useCallback(
        (isNowOpen: boolean) => {
            if (!isNowOpen) {
                handleCancel();
            }
        },
        [handleCancel]
    );

    return (
        <Modal isOpen={isOpen} onOpenChange={handleOpenChange}>
            <ModalContent role="alertdialog" size="md" closeButton={false}>
                <ModalHeader className="px-6 pt-6 pb-2">
                    <ModalTitle className="text-xl tracking-tight sm:text-xl">{title}</ModalTitle>
                    {description && <ModalDescription className="mt-1">{description}</ModalDescription>}
                </ModalHeader>
                {confirmationLabel && (
                    <ModalBody className="px-6">
                        <CheckboxField isSelected={isAffirmed} onChange={setIsAffirmed} isDisabled={isConfirming}>
                            <Checkbox>{confirmationLabel}</Checkbox>
                        </CheckboxField>
                    </ModalBody>
                )}
                <ModalFooter className="px-6 py-4">
                    <Button intent="plain" onPress={handleCancel} isDisabled={isConfirming} name={cancelName}>
                        {cancelLabel}
                    </Button>
                    <Button intent={confirmIntent} onPress={onConfirm} isPending={isConfirming} isDisabled={!!confirmationLabel && !isAffirmed} name={confirmName}>
                        {isConfirming && <ProgressCircle isIndeterminate aria-hidden="true" />}
                        {confirmLabel}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
